'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import type { ProductDisplayData as Product } from '@eliadmin/core/src/api/products/types/product-types';
import { AddToCartButton } from '@eliadmin/core/src/components/product/add-to-cart-button';
import { AddToFavoritesButton } from '@eliadmin/core/src/components/product/add-to-favorites-button';
import { ProductPrice } from '@eliadmin/core/src/components/product/product-price';
import { Badge } from '@eliadmin/core/src/components/ui/badge';
import { Card, CardContent, CardFooter } from '@eliadmin/core/src/components/ui/card';
import { DialogProvider } from '@eliadmin/core/src/components/ui/dialog';
import { useCategoryStore } from '@eliadmin/core/src/stores/category-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { VariantsProvider, useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import {
  getProductFilterVariants,
  getProductImage,
  getProductUrl,
  isSellable,
} from '@eliadmin/core/src/utils/product-utils';
import { cn, getContrastYIQ } from '@eliadmin/core/src/utils/style-utils';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import { createContext, Fragment, memo, useContext, useMemo } from 'react';
export type ProductCardProps = {
  product: Product;
  deferImageMedia?: string;
  className?: {
    main?: string;
  };
};

// Scoped by the category grid: unrelated carousels/cards keep lazy loading.
export const ProductImagePriorityContext = createContext<Product | null>(null);

const CARD_PRICE_CLASSES = {
  main: 'items-center ea-product-card__product-price',
  discountWrapper: 'absolute left-3 top-4',
  priceAfterDiscount: 'text-[18px]',
};

function ProductCardInternal({ product, className, deferImageMedia }: ProductCardProps) {
  const priorityProduct = useContext(ProductImagePriorityContext);
  const imagePriority = priorityProduct !== null && priorityProduct.id === product.id;
  //   const isAuth = useGlobalStore(state => state.config.isAuth);
  const templateSettings = useGlobalStore(s => s.templateSettings);
  const productTeaserSettings = templateSettings?.product_teaser;
  // const api = useGlobalStore(state => state.config.api);
  // Pagination/sort/price filters cannot change the variant thumbnail.
  const attributes = useCategoryStore(state => state.filter.attributes);
  const selectedValues = useVariantsStore(state => state.selectedValues);

  const image = useMemo(() => {
    const filterVariants = getProductFilterVariants(product, { attributes } as Parameters<
      typeof getProductFilterVariants
    >[1]);
    return getProductImage(templateSettings, product, filterVariants);
  }, [product, attributes, templateSettings]);
  const productUrl = useMemo(
    () => getProductUrl(product, selectedValues),
    [product, selectedValues],
  );

  return (
    <Card
      className={cn(
        // shadow-[0_-2px_60px_-15px_rgba(0,0,0,0.15)] removed, kept it there because of nice regex pattern
        'relative flex h-full min-w-[250px] flex-col gap-2 border border-border',
        'ea-product-card',
        className?.main,
      )}
    >
      {!!productTeaserSettings?.favorite_icon?.enabled && (
        <div className={cn('absolute right-4 top-4 z-10', 'ea-product-card__add-to-favorites')}>
          <AddToFavoritesButton slug={product.slug} isFavorite={product.is_favorite ?? false} />
        </div>
      )}

      <div
        className={cn(
          'absolute right-4 top-4 z-10 flex flex-col flex-wrap items-end gap-1 text-xs',
          'ea-product-card__badges',
        )}
      >
        {product.tags?.map((badge, i) => (
          <Fragment key={`badge-${i}`}>
            <Badge
              style={{
                backgroundColor: badge.color ?? '#000000',
                color: getContrastYIQ(badge.color ?? '#000000'),
              }}
              size="sm"
              className={cn('ea-product-card__badge')}
            >
              {badge.name}
            </Badge>
          </Fragment>
        ))}
      </div>

      <CardContent
        className={cn('flex flex-1 flex-col gap-2 p-3 pb-0', 'ea-product-card__content')}
      >
        {image?.url && (
          <Link prefetchOnHover href={productUrl} aria-label={product.name} className="">
            <Image
              alt={resolveImageAlt(image, product?.name)}
              src={image.url}
              width="0"
              height="0"
              sizes="350px"
              loading={imagePriority ? 'eager' : 'lazy'}
              fetchPriority={imagePriority ? 'high' : undefined}
              deferUntilNearViewport={!!deferImageMedia && !imagePriority}
              deferViewportMedia={deferImageMedia}
              className="aspect-square w-full rounded-lg object-contain"
            />
          </Link>
        )}

        <Link
          prefetchOnHover
          href={productUrl}
          className={cn('text-center text-lg font-bold', 'ea-product-card__name')}
        >
          {product.name}
        </Link>

        {/* Modra permanently hides teaser descriptions; keep rich HTML on the product detail. */}
      </CardContent>

      {/* Modra permanently hides stock/detail presentation in CSS; do not mount those subtrees. */}
      <CardFooter
        className={cn('flex flex-col justify-end gap-2 p-3 pt-0', 'ea-product-card__footer')}
      >
        {/* Price */}
        {/* <Price product={product} filterVariants={filterVariants} /> */}
        <ProductPrice product={product} showDiscountBadge={false} className={CARD_PRICE_CLASSES} />

        <div className="flex w-full gap-2">
          {!!productTeaserSettings?.cart_button?.enabled && (
            <AddToCartButton
              product={product}
              showIcon={!!productTeaserSettings?.cart_icon}
              /** TODO: sellable */
              sellable={isSellable(product, 1)}
              className={cn('flex-grow', 'ea-product-card__add-to-cart-button')}
              // variant="accent"
            />
          )}
          {/* {templateSettings.product_teaser.favorite_icon.enabled && (
            <div className={cn('', 'ea-product-card__add-to-favorites')}>
              <AddToFavoritesButton slug={product.slug} isFavorite={product.is_favorite ?? false} />
            </div>
          )} */}
        </div>
      </CardFooter>
    </Card>
  );
}

export const ProductCard = memo(
  function ProductCard(props: ProductCardProps) {
    return (
      <VariantsProvider state={{ product: props.product }}>
        <DialogProvider>
          <ProductCardInternal {...props} />
        </DialogProvider>
      </VariantsProvider>
    );
  },
  (previous, next) =>
    previous.product === next.product &&
    previous.deferImageMedia === next.deferImageMedia &&
    previous.className?.main === next.className?.main,
);
